import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RatingSelectorProps {
  value: number | null;
  onChange: (rating: number) => void;
  className?: string;
  disabled?: boolean;
}

export const RatingSelector: React.FC<RatingSelectorProps> = ({
  value,
  onChange,
  className = "",
  disabled = false
}) => {
  const handleClick = (rating: number) => {
    if (!disabled) {
      // If clicking the same rating, set it to 0 (no rating)
      if (value === rating) {
        onChange(0);
      } else {
        onChange(rating);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, rating: number) => {
    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleClick(rating);
    }
  };

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div className="flex items-center justify-between w-full px-4 gap-1">
        {[1, 2, 3, 4, 5].map((rating) => (
          <button
            key={rating}
            type="button"
            onClick={() => handleClick(rating)}
            onKeyDown={(e) => handleKeyDown(e, rating)}
            disabled={disabled}
            className={cn(
              "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-[#D72E16]",
              value && value >= rating
                ? "bg-[#D72E16] text-white hover:bg-[#B8251A]"
                : "bg-gray-200 text-gray-600 hover:bg-gray-300",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            aria-label={`Rate ${rating} out of 5`}
            tabIndex={disabled ? -1 : 0}
          >
            {rating}
          </button>
        ))}
      </div>
    </div>
  );
}; 